import { inject, Injectable, signal } from '@angular/core';
import html2canvas from 'html2canvas';
import { jsPDF as JsPDF } from 'jspdf';
import { SECONDS } from 'src/app/shared/functions/time';
import { timePromise } from 'src/app/shared/functions/time-promise';

import { ChartsService } from '../charts-service';
import { DownloadChartsPdfDialogService } from './download-charts-pdf-dialog/download-charts-pdf-dialog.service';
import { DownloadChartsPdfSnackbarService } from './download-charts-pdf-snackbar/download-charts-pdf-snackbar.service';

const REPORT_NAME = 'Operations-Summary-Report';

@Injectable()
export class DownloadChartsPdfService {
  private readonly snackbarService = inject(DownloadChartsPdfSnackbarService);

  private readonly dialogService = inject(DownloadChartsPdfDialogService);

  private readonly isDownloadingSignal$ = signal(false);

  readonly isDownloading = this.isDownloadingSignal$.asReadonly();

  async downloadCharts(charts: ChartsService): Promise<void> {
    if (this.isDownloadingSignal$()) {
      return;
    }

    let overlays: ReturnType<typeof this.openOverlays> | undefined;
    this.isDownloadingSignal$.set(true);

    try {
      overlays = this.openOverlays(charts);

      await timePromise(SECONDS(0.5));
      overlays.snackbarDetails = 'Generating PDF...';

      await timePromise(SECONDS(1.5));
      await this.captureHtmlToPdf(overlays.reportContainerElement);
      this.isDownloadingSignal$.set(false);
      overlays.dispose();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error generating PDF:', error);
      this.isDownloadingSignal$.set(false);
      overlays?.dispose();
    }
  }

  async captureHtmlToPdf(element: HTMLElement | null): Promise<void> {
    if (element === null) {
      return;
    }

    this.cleanElementDom(element);
    const canvasWidth = element.offsetWidth + 100;

    const canvas = await html2canvas(element, {
      backgroundColor: '#F8F8F8',
      useCORS: true,
    });

    const canvasHeight = canvas.height;
    const canvasDataURL = canvas.toDataURL('image/png');

    const pdf = new JsPDF({
      format: [canvasWidth, canvasHeight],
      orientation: 'portrait',
      unit: 'px',
    });

    pdf.addImage(canvasDataURL, 'PNG', 0, 0, canvasWidth, canvasHeight);
    pdf.save(`${REPORT_NAME}.pdf`);
  }

  /**
   * Open the overlays for the snackbar and the dialog
   * @important This overlays should NOT Block Navigation
   * @param chartsService - The charts to download
   * @returns An object with the dispose function and the snackbar message setter
   */
  private openOverlays(chartsService: ChartsService) {
    const snackbarApi = this.snackbarService.createSnackbar({
      message: 'Preparing Charts...',
    });
    const dialogApi = this.dialogService.openDialog({
      chartsService,
    });

    return {
      dispose() {
        snackbarApi.dispose();
        dialogApi.dispose();
      },
      get reportContainerElement() {
        return dialogApi.htmlElement;
      },
      set snackbarDetails(value: string) {
        snackbarApi.details = value;
      },
    };
  }

  /**
   * Clean the DOM of the element to avoid the shadow and the header
   * for the PDF generation to be cleaner
   * @param element - The element to clean
   */
  private cleanElementDom(element: HTMLElement) {
    const statsElements = Array.from(
      element.querySelectorAll<HTMLElement>('.stats'),
    );

    statsElements.forEach((el) => {
      el.style.boxShadow = 'none';
      el.querySelector('&>:nth-child(3)')?.remove();
    });

    const headerElements = Array.from(
      element.querySelectorAll<HTMLElement>('div > div > h1'),
    );

    headerElements.forEach((el) => {
      el.parentElement.style.justifyContent = 'center';
      el.parentElement.style.border = 'none';

      el.parentElement.querySelectorAll('& > div').forEach((x: HTMLElement) => {
        x.remove();
      });
    });
  }
}
